import { isEmpty } from "es-toolkit/compat";

/**
 * Recursively checks if all values in an object are empty strings, undefined, or empty objects.
 *
 * @example
 * ```typescript
 * areAllValuesEmpty({ a: "", b: { c: "" } }) // returns true
 * areAllValuesEmpty({ a: "", b: { c: "value" } }) // returns false
 * areAllValuesEmpty({ a: "", b: undefined }) // returns true
 * areAllValuesEmpty({ a: "", b: { c: { d: "" } } }) // returns true
 * ```
 *
 * @param obj - The object to check
 * @returns true if all values are empty, false otherwise
 */
export const areAllObjectValuesEmpty = (obj: Record<string, any>): boolean => {
  if (!obj || typeof obj !== "object" || isEmpty(obj)) {
    return true;
  }

  return Object.values(obj).every((value) => {
    // Handle nested objects
    if (typeof value === "object") {
      // Arrays with any non-empty values are considered non-empty
      if (Array.isArray(value)) {
        return (
          value.length === 0 ||
          value.every((item) =>
            typeof item === "object"
              ? areAllObjectValuesEmpty(item)
              : item === "" || item === undefined
          )
        );
      }
      // Handle nested objects that have properties
      if (!isEmpty(value)) {
        return areAllObjectValuesEmpty(value);
      }
      return true;
    }
    // Handle primitive values
    return value === "" || value === undefined;
  });
};
